/**
 * portNameSync -- the pure logic behind switch port NAMES now that Meridian is their source of
 * truth (owner decision 2026-09-14, option C "Meraki auto-sync").
 *
 * WHAT CHANGED
 * A name is no longer part of a previewed CLI change. It is saved in Meridian immediately
 * (POST /api/v2/switch/port-names, PUT .../port-labels/<port>) and the controller writes it to
 * the switch as the port `description` in the background -- one CLI session per switch, then
 * save (services/port_name_sync). A name changed directly on the switch is overwritten.
 *
 * WHAT IS NOT AUTOMATIC (coordinator, 2026-09-14)
 *   * names on a switch that is NOT ASSIGNED TO A SITE (`auto_sync: false`) are stored and wait;
 *   * IMPORTED names -- labels saved before auto-sync existed, possibly stale (the ECS4150-54P's two
 *     gateway labels after it moved to the office) -- are never written on their own.
 * Both are sent by the explicit msp_admin action "Write names to switch" (or, for an imported
 * name, by saving that name again). So the badge offers that action instead of Retry there.
 *
 * THE BADGES. Synced / Syncing / Waiting / Failed are OPERATIONAL state (does the switch hold the
 * name yet?) and use the status ramp. "Not written to the switch yet" is not a fault or a delay --
 * nothing was attempted -- so it is neutral, like "Never seen" for a dormant device.
 *   Synced                         healthy   the switch accepted it
 *   Syncing                        info      queued (quiet window) or being written now
 *   Waiting for switch             warn      could not reach the switch (backoff, reconnect), or the
 *                                            switch isn't assigned to a site; reason shown
 *   Failed                         critical  the switch REFUSED the line; the switch's words shown
 *   Not written to the switch yet  neutral   imported, never written by Meridian
 *
 * Everything here is a pure function (no React, no fetch) so the mapping and the bulk-name rules
 * are pinned by `npm run test:unit` (src/lib/portNameSync.test.ts).
 */
import { MAX_PORT_NAME, expandNamePattern, sanitizePortName } from "./portTable.ts";
import type { BulkEdit, PortRow } from "./portTable.ts";

/** Per-port sync state as the API reports it (services/port_name_sync sync view). */
export interface PortNameSync {
  state: "imported" | "synced" | "pending" | "failed";
  /** What the switch was last confirmed to hold; null = never confirmed. */
  synced_name: string | null;
  /** Plain words: "Switch unreachable: ..." / "Switch isn't assigned to a site" / a refused line. */
  last_error: string | null;
  attempts: number;
  next_attempt_at: string | null;
  synced_at: string | null;
  /** A CLI session for this switch is running right now. */
  running?: boolean;
  /** false = the switch has no site: nothing is written automatically. */
  auto_sync?: boolean;
}

export type NameBadgeKind = "imported" | "synced" | "syncing" | "waiting" | "failed";

/** What the badge renders. `tone` is a status-ramp token, or neutral for "nothing attempted". */
export interface NameBadge {
  kind: NameBadgeKind;
  label: string;
  tone: "neutral" | "healthy" | "info" | "warn" | "critical";
  /** Longer plain-words explanation (tooltip / sheet), or null. */
  detail: string | null;
  /**
   * The action offered (msp_admin only; the component decides who sees it):
   *   retry  POST .../name-sync/retry  (automatic sync, switch assigned to a site)
   *   write  POST .../name-sync/write  ("Write names to switch": explicit, any switch)
   */
  action: "retry" | "write" | null;
}

/** "in 4 min" / "in 45 s" / "now" for a next-attempt time. PURE (`now` injectable). */
export function formatNextTry(iso: string | null | undefined, now: number = Date.now()): string | null {
  if (!iso) return null;
  // The API emits naive UTC ISO strings (timeutil.utcnow); treat a zone-less value as UTC.
  const at = Date.parse(/[zZ]|[+-]\d\d:\d\d$/.test(iso) ? iso : `${iso}Z`);
  if (Number.isNaN(at)) return null;
  const secs = Math.round((at - now) / 1000);
  if (secs <= 5) return "now";
  if (secs < 60) return `in ${secs} s`;
  const mins = Math.round(secs / 60);
  if (mins < 60) return `in ${mins} min`;
  const hours = Math.round(mins / 60);
  return `in ${hours} h`;
}

/**
 * The badge for one port's sync state, or null when Meridian holds no name for the port.
 *
 * pending splits on `last_error`: without one the name is simply on its way (Syncing); with one
 * the last session could not reach the switch, or the switch has no site (Waiting for switch). A
 * session running right now always reads Syncing, including a retry of a waiting name.
 */
export function nameSyncBadge(sync: PortNameSync | null | undefined, now: number = Date.now()): NameBadge | null {
  if (!sync) return null;
  // Retry only makes sense where sync is automatic; everywhere else the explicit write is the way.
  const againAction: NameBadge["action"] = sync.auto_sync === false ? "write" : "retry";
  if (sync.state === "imported") {
    return {
      kind: "imported",
      label: "Not written to the switch yet",
      tone: "neutral",
      detail:
        "Saved before names synced automatically, and never written by Meridian — it may be out of date. " +
        "Clear it, save it again, or use Write names to switch to send it.",
      action: "write",
    };
  }
  if (sync.state === "failed") {
    return {
      kind: "failed",
      label: "Failed",
      tone: "critical",
      detail: sync.last_error || "The switch refused this name.",
      action: againAction,
    };
  }
  if (sync.state === "pending") {
    if (sync.running || !sync.last_error) {
      return {
        kind: "syncing",
        label: "Syncing",
        tone: "info",
        detail: sync.running ? "Writing to the switch now." : "Queued — it goes to the switch in a few seconds.",
        action: null,
      };
    }
    const next = formatNextTry(sync.next_attempt_at, now);
    return {
      kind: "waiting",
      label: "Waiting for switch",
      tone: "warn",
      detail: next ? `${sync.last_error} · next try ${next}` : sync.last_error,
      action: againAction,
    };
  }
  // synced. last_error here is only ever the "save not confirmed" note -- still shown on hover.
  return { kind: "synced", label: "Synced", tone: "healthy", detail: sync.last_error || null, action: null };
}

/** True while any port's name is on its way (queued or running): the port view polls faster. */
export function namesInFlight(ports: readonly { name_sync?: PortNameSync | null }[] | undefined): boolean {
  return !!ports?.some((p) => nameSyncBadge(p.name_sync)?.kind === "syncing");
}

/**
 * Names on this switch that Meridian will NOT write on its own -- what the device sheet's
 * "Write names to switch" callout counts. imported: never written; unassigned: waiting or failed
 * on a switch with no site.
 */
export function namesNotWritten(ports: readonly { name_sync?: PortNameSync | null }[] | undefined): {
  imported: number;
  unassigned: number;
  total: number;
} {
  let imported = 0;
  let unassigned = 0;
  for (const p of ports ?? []) {
    const s = p.name_sync;
    if (!s) continue;
    if (s.state === "imported") imported += 1;
    else if (s.auto_sync === false && s.state !== "synced") unassigned += 1;
  }
  return { imported, unassigned, total: imported + unassigned };
}

/** One entry of the batch name API. `name: ""` clears the port's name. */
export interface PortNameItem {
  serial: string;
  port: string;
  name: string;
}

/** Control characters: the backend refuses them (a line break would be a second CLI line). */
const CONTROL = /[\u0000-\u001f\u007f]/;

/**
 * The bulk editor's Name field -> batch name items, or validation errors.
 *
 * Only ports whose name actually CHANGES are sent. A failed or imported port is sent even with
 * the same name: saving it again is asking again (failed) or confirming it (imported).
 * Ordered by switch then faceplate, like the table.
 */
export function buildNameItems(
  rows: readonly PortRow[],
  edit: Pick<BulkEdit, "nameMode" | "namePattern">,
): { items: PortNameItem[]; errors: string[] } {
  const errors: string[] = [];
  if (edit.nameMode === "keep") return { items: [], errors };
  if (edit.nameMode === "set" && !sanitizePortName(edit.namePattern)) {
    errors.push("Name: type a name or a pattern, or choose No change");
  }
  if (edit.nameMode === "set" && CONTROL.test(edit.namePattern)) {
    errors.push("Name: line breaks and tabs are not allowed");
  }
  const ordered = [...rows].sort(
    (a, b) => a.switchName.localeCompare(b.switchName, undefined, { numeric: true }) || a.order - b.order,
  );
  const items: PortNameItem[] = [];
  for (const r of ordered) {
    const name = edit.nameMode === "set" ? expandNamePattern(edit.namePattern, { port: r.number, switchName: r.switchName }) : "";
    if (name.length > MAX_PORT_NAME) {
      errors.push(`Name for ${r.switchName} port ${r.number} is longer than ${MAX_PORT_NAME} characters`);
    }
    const current = sanitizePortName(r.port.alias ?? "");
    const state = r.port.name_sync?.state;
    const confirmAgain = state === "failed" || state === "imported";
    if (name === current && !(confirmAgain && name !== "")) continue;
    items.push({ serial: r.serial, port: r.port.name, name });
  }
  return { items: errors.length ? [] : items, errors: [...new Set(errors)] };
}

/** The batch API's answer. */
export interface PortNamesBatchResponse {
  saved: number;
  switches: { serial: string; changed: string[]; due_at: string | null; auto_sync?: boolean }[];
}

/**
 * "6 names saved · syncing to 2 switches" -- the line under the Save button. PURE.
 * Switches without a site are called out: their names are stored but not written.
 */
export function describeNameSave(res: PortNamesBatchResponse): string {
  if (!res.saved) return "No names changed.";
  const touched = res.switches.filter((s) => s.changed.length > 0);
  const auto = touched.filter((s) => s.auto_sync !== false).length;
  const held = touched.length - auto;
  const parts = [`${res.saved} name${res.saved === 1 ? "" : "s"} saved`];
  if (auto) parts.push(`syncing to ${auto} switch${auto === 1 ? "" : "es"}`);
  if (held) {
    parts.push(
      `${held} switch${held === 1 ? " isn't" : "es aren't"} assigned to a site — not written until assigned or you use Write names to switch`,
    );
  }
  return parts.join(" · ");
}
